import { DocumentNode } from 'graphql';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Tag, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';

import {
  GetInvitationQuery as GetInvitation,
  GetInvitationsQuery as GetInvitations,
  PurchaseStatus,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateInvitationMutation from './ActivateInvitationMutation';
import DeactivateInvitationMutation from './DeactivateInvitationMutation';
import DeleteInvitationMutation from './DeleteInvitationMutation';
import EditInvitationForm from './EditInvitationForm';
import GetInvitationsQuery from './GetInvitationsQuery';
import InvitationStatusIndicator from './InvitationStatusIndicator';
import ShareInfo from './ShareInfo';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../../common/useProject';

  type Event = GetInvitations['project']['event'];
  type Invitation = Event['invitations'][0];

interface InvitationListProps {
  event: Event;
  onResetFilters: () => void;
  activeInvitationId: string;
  invitationData?: GetInvitation;
  refetchQueries?: DocumentNode[];
}

const InvitationList = ({
  event, onResetFilters, activeInvitationId, invitationData, refetchQueries = [GetInvitationsQuery],
}: InvitationListProps) => {
  const project = useProject();
  const { t } = useTranslation();

  const hasFilteredInvitations = event.invitations.length > 0;

  return (
    <UI.Div>
      {hasFilteredInvitations ? (
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {event.invitations.map((invitation, index) => (
            <UI.CollapsibleNavCard
              id={`InvitationCard_${invitation.id}`}
              key={index}
              to={`/${project.id}/events/${event.id}/invitation-codes/${activeInvitationId !== invitation.id ? invitation.id : ''}`}
              open={invitation.id === activeInvitationId}
              muted={!!activeInvitationId && invitation.id !== activeInvitationId}
              preview={(
                <InvitationPreview
                  invitation={invitation}
                  event={event}
                  refetchQueries={refetchQueries}
                />
              )}
              body={invitationData
                ? <InvitationBody invitationData={invitationData} />
                : <UI.PageLoader />}
            />
          ))}
        </UI.GridContainer>
      ) : (
        <UI.Card
          sc={{ padding: 8, textAlign: 'center' }}
        >
          <UI.Div sc={{ color: 'secondary', mb: 2 }}>
            <Tag strokeWidth={0.5} size={60} />
          </UI.Div>
          <UI.Span>{t('no_invitations_match_filters')}</UI.Span>
          {' '}
          <UI.A onClick={onResetFilters}>{t('reset_filters')}</UI.A>
        </UI.Card>
      )}
    </UI.Div>
  );
};

interface InvitationPreviewProps {
  invitation: Invitation;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const InvitationPreview = ({ invitation, event, refetchQueries }: InvitationPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const [activateInvitation] = useMutation(ActivateInvitationMutation, {
    variables: {
      id: invitation.id,
    },
    optimisticResponse: {
      activateInvitation: {
        id: invitation.id,
        active: true,
        __typename: 'Invitation',
      },
    },
    onCompleted: () => notifier.success(t('notifications.invitation_activated')),
  });

  const [deactivateInvitation] = useMutation(DeactivateInvitationMutation, {
    variables: {
      id: invitation.id,
    },
    optimisticResponse: {
      deactivateInvitation: {
        id: invitation.id,
        active: false,
        __typename: 'Invitation',
      },
    },
    onCompleted: () => notifier.success(t('notifications.invitation_deactivated')),
  });

  const toggleStatus = () => {
    if (invitation.active) {
      deactivateInvitation();
    } else {
      activateInvitation();
    }
  };

  const [deleteInvitation, { loading: deleting }] = useMutation(
    DeleteInvitationMutation, {
      variables: {
        id: invitation.id,
      },
      onCompleted: () => {
        history.push(`/${project.id}/events/${event.id}/invitation-codes`);
        notifier.success(t('notifications.invitation_deleted'));
      },
      onError: () => notifier.error(t('something_went_wrong')),
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: invitation.prefix }),
    onConfirm: () => deleteInvitation(),
  }, [invitation]);

  const muted = !invitation.active;

  return (
    <ContainerWithOptions
      name={invitation.prefix}
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!invitation.deletable || deleting}
        />
      )}
    >
      <UI.GridContainer sc={{ columns: ['1fr 1fr', '3fr 1fr 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div sc={{ muted }}>
          <UI.Div>
            <UI.Label>
              <UI.Strong>{invitation.prefix}</UI.Strong>
              {invitation.has_unique_codes && (
                <UI.Span>
                  &middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;
                </UI.Span>
              )}
            </UI.Label>
          </UI.Div>
          <UI.Delimit>
            {invitation.tickets.length === 0 && t('all_tickets')}
            {!device.isSmall && invitation.tickets.length > 0 && (
              <UI.Span>{invitation.tickets[0].title}</UI.Span>
            )}
            {!device.isSmall && invitation.tickets.length > 1 && (
              <UI.Span>
                {' '}
                {t('and_n_more', {
                  n: formatInteger(invitation.tickets.length - 1),
                  count: invitation.tickets.length - 1,
                })}
              </UI.Span>
            )}
            {device.isSmall && invitation.tickets.length > 0 && (
              t('n_tickets', {
                count: invitation.tickets.length,
                value: formatInteger(invitation.tickets.length),
              })
            )}
          </UI.Delimit>
        </UI.Div>

        {!device.isSmall && (
          <UI.Div sc={{ muted }} style={{ gridColumn: '2 / span 2' }}>
            <UI.Div sc={{ fontWeight: 500 }}>
              {formatInteger(invitation.uses)}
              {invitation.max_uses !== null && (
                <UI.Sup sc={{ muted: true }}>
                  {` / ${formatInteger(invitation.max_uses)}`}
                </UI.Sup>
              )}
            </UI.Div>
            <UI.Link
              to={`/${project.id}/participants?${encodeUrl({
                event: event.id,
                invitations: [invitation.id],
                statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
              })}`}
              sc={{ secondary: true }}
            >
              {t('times_used', { count: invitation.uses })}
            </UI.Link>
          </UI.Div>
        )}

        <UI.Div sc={{ textAlign: 'right', small: true }}>
          <UI.Toggle checked={invitation.active} onChange={() => toggleStatus()} />

          <UI.Truncate sc={{ color: 'gray.500' }}>
            <InvitationStatusIndicator invitation={invitation} />
          </UI.Truncate>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

interface InvitationBodyProps {
  invitationData: GetInvitation;
}

const InvitationBody = ({ invitationData: { invitation, event } }: InvitationBodyProps) => {
  const { t } = useTranslation();
  const project = useProject();

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
      <UI.Nav>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/invitation-codes/${invitation.id}`}
          exact
        >
          {t('settings')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/invitation-codes/${invitation.id}/share`}
        >
          {t('share')}
        </UI.NavLink>
      </UI.Nav>

      <UI.Div>
        <Switch>
          <Route path="/:projectId/events/:eventId/invitation-codes/:invitationId/share">
            <ShareInfo invitation={invitation} />
          </Route>
          <Route path="/:projectId/events/:eventId/invitation-codes/:invitationId">
            <EditInvitationForm invitation={invitation} event={event} />
          </Route>
        </Switch>
      </UI.Div>
    </UI.FormGrid>
  );
};

export default InvitationList;
